import React from 'react';
import { Box, Card, Typography, Grid, Pagination } from '@mui/material';
import useSWR from 'swr';
import { getRewardPointBalance, getRewardPoints } from '../../../api/RewardPoint';
import { PATH_NAME } from '../../../configs/pathName';
import colorConfigs from '../../../configs/colorConfigs';
import { typo } from '../../../assets/fonts/font';
import { useState } from 'react';

const RewardPointsPage: React.FC = () => {
  const [page, setPage] = useState<number>(1);
  const items_per_page = 5;
  const { data: rewardPoints, error: pointsError, isLoading: pointsLoading } = useSWR(PATH_NAME.API_USER_GET_REWARD_POINTS, getRewardPoints);
  const { data: balance, error: balanceError, isLoading: balanceLoading } = useSWR(PATH_NAME.API_USER_GET_REWARD_POINTS_BALANCE, getRewardPointBalance);

  if (pointsLoading || balanceLoading) return <div>Loading...</div>;
  if (pointsError || balanceError) return <div>Error loading reward points or balance</div>;

  const filteredRewardPoints = (rewardPoints || []).filter((point: any) => point.points !== 0);
  const totalPages = Math.max(1, Math.ceil(filteredRewardPoints.length / items_per_page));
  const paginatedRewardPoints = filteredRewardPoints.slice(
    (page - 1) * items_per_page,
    page * items_per_page
  );

  return (
    <Box sx={{ p: 3, backgroundColor:colorConfigs.secondary, minHeight: '100vh' }}>   
      <Card
        sx={{
          p: 3,
          textAlign: 'center',
          backgroundColor: colorConfigs.quaternary,
          borderRadius: '15px',
          borderStyle:'solid',
          borderWidth:2,
          mb: 2
        }}>
        <Typography variant="body1" sx={{...typo.bodyContent, color: colorConfigs.tertiary}}>Your Balance</Typography>
        <Typography variant="h2"  sx={{...typo.headline, color: colorConfigs.primary, fontWeight: 'bold'}}>
          {balance}
        </Typography>
        <Typography variant="body2" sx={{...typo.bodyContent, color: colorConfigs.tertiary}}>Points</Typography>
      </Card>

      <Grid container spacing={0}>
        {filteredRewardPoints.length > 0 ? (
          paginatedRewardPoints.map((point: any) => (
            <Grid item xs={12} key={point.id}>
              <Card
                sx={{ 
                  p: 2,
                  backgroundColor: colorConfigs.quaternary,
                  borderRadius: '15px',
                  borderStyle:'solid',
                  borderWidth:2,
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1
                }}>
                <Box
                  sx={{
                    width: 6,
                    height: 45,
                    backgroundColor: point.points >= 0 ? colorConfigs.primary : colorConfigs.rewardNegative,
                    borderRadius: 1,
                    mr: 2,
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ ...typo.bodyContent, fontWeight: 'bold' }}>
                    {point.type === 'debit' ? 'Redeemed' : point.type === 'credit' ? 'Earned' : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ ...typo.bodyContent, color: colorConfigs.tertiary  }}>
                    {new Date(point.transaction_at).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" sx={{ ...typo.bodyContent, color: colorConfigs.tertiary }}>
                    {point.description}
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ ...typo.bodyContent, fontWeight: 'bold', color: point.points >= 0 ? colorConfigs.primary : colorConfigs.rewardNegative }}>
                  {point.type === 'debit' ? `-${point.points}` : point.type === 'credit' ? `+${point.points}` : ''} Points
                </Typography>
              </Card>
            </Grid>
          ))
        ) : (
          <Typography variant="body2" sx={{ ...typo.bodyContent, textAlign: 'center', mt: 2 }}>
            No reward points available
          </Typography>
        )}
      </Grid>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            sx={{
              '& .MuiPaginationItem-root': {
                color: colorConfigs.tertiary,
              },
              '& .MuiPaginationItem-root.Mui-selected': {
                color: colorConfigs.secondary,
                backgroundColor: colorConfigs.primary,
              },
              '& .MuiPaginationItem-root.Mui-selected:hover, & .MuiPaginationItem-root.Mui-selected:focus-visible': {
                color: colorConfigs.secondary,
                backgroundColor: colorConfigs.primary,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default RewardPointsPage;
